import ClearIcon from '@mui/icons-material/Clear'
import { Grid, IconButton, InputAdornment, TextField } from '@mui/material'
import { ReactNode } from 'react'
import type { PaginationProps, SearchProps } from 'shared/hooks'
import Pagination from '../Pagination'
import StepEnterScrollTop from '../StepEnterScrollTop'
import {
  NoResults,
  PaginationWrapper,
  ScrollArea,
  SearchRow,
  Wrapper,
} from './PaginatedListStep.style'
import PageLoader from '../PageLoader'

const SCROLL_CONTAINER_ATTR = 'data-paginated-list-step-scroll'

interface PaginatedListStepProps {
  children: ReactNode
  contentLength: number
  loading: boolean
  noResultsLabel: string
  pagination: PaginationProps
  search?: SearchProps & { label: string }
  onSearchChange?: () => void
  paginationVisibleLabel?: boolean
  disableMargin?: boolean
}

const PaginatedListStep = ({
  children,
  contentLength,
  loading,
  noResultsLabel,
  pagination,
  search,
  onSearchChange,
  paginationVisibleLabel,
  disableMargin = false,
}: PaginatedListStepProps) => {
  const handleChange = (callback: () => void) => {
    if (!loading) callback()
  }

  const handleSearchChange = (query: string) => {
    if (!search || query === search.query) return

    onSearchChange?.()
    search.setQuery(query)
  }

  return (
    <Wrapper sx={disableMargin ? { margin: 0 } : undefined}>
      {search && (
        <SearchRow container>
          <Grid item xs={12} sm={12} md={9} lg={7} xl={6}>
            <TextField
              fullWidth
              label={search.label}
              size="small"
              value={search.query}
              onChange={event => handleSearchChange(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                }
              }}
              InputProps={{
                endAdornment: search.query ? (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      size="small"
                      onMouseDown={event => event.preventDefault()}
                      onClick={() => handleSearchChange('')}
                    >
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
            />
          </Grid>
        </SearchRow>
      )}

      <ScrollArea {...{ [SCROLL_CONTAINER_ATTR]: true }}>
        <StepEnterScrollTop scrollContainerAttr={SCROLL_CONTAINER_ATTR} />

        {loading ? (
          <PageLoader />
        ) : (
          <>
            {children}
            {contentLength === 0 && (
              <NoResults variant="h6">{noResultsLabel}</NoResults>
            )}
          </>
        )}
      </ScrollArea>

      <PaginationWrapper>
        <Pagination
          {...pagination}
          visibleLabel={paginationVisibleLabel}
          canGoNextPage={!loading && pagination.canGoNextPage}
          canGoPrevPage={!loading && pagination.canGoPrevPage}
          goNextPage={() => handleChange(pagination.goNextPage)}
          goPrevPage={() => handleChange(pagination.goPrevPage)}
          goFirstPage={() => handleChange(pagination.goFirstPage)}
          changePageSize={pageSize => {
            if (loading || pageSize === pagination.onPage) return
            pagination.changePageSize(pageSize)
          }}
          pageCount={Math.max(1, pagination.pageCount)}
        />
      </PaginationWrapper>
    </Wrapper>
  )
}

export default PaginatedListStep
